import CustomWebView from '@/components/CustomWebView/ResultsCharts';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { logError } from '@/utils/logHandling';
import { StackScreenProps } from '@react-navigation/stack';
import { useEffect, useRef } from 'react';
import { Linking, Platform, View } from 'react-native';
import { Text } from '@/components/Text';
import { isPatientApp } from '@/config/constants';
import { useDialog } from '@/hooks/useDialog';
import type { DialogId } from '@/components/ui/shared/Dialog/types';
import { Links } from '@/config/links';
import theme from '@/config/theme';

import { styles } from './styles';

interface ResultDetailProps extends StackScreenProps<
  TherapistStackParamList,
  'ResultDetailScreen'
> {
  handleDownloadLinksClickState: boolean;
  changeHandleDownloadLinksClickState: (arg: boolean) => void;
  timeSeriesData: unknown;
  individualStatsData: unknown;
  webViewTitle: string;
}

const html2canvas = Platform.select({
  // On Native just adding this library since in both cases the html2canvas will be implemented inside the web only
  /* eslint-disable @typescript-eslint/no-require-imports */
  native: () => require('@/components/Util/reactResponsiveDimensions'),
  default: () => require('html2canvas'),
})();

export const ResultDetail = ({
  route,
  handleDownloadLinksClickState,
  changeHandleDownloadLinksClickState,
  timeSeriesData,
  individualStatsData,
  webViewTitle,
}: ResultDetailProps) => {
  const dataType = useRef('');
  const exportRef = useRef<HTMLIFrameElement | null>(null);
  const { showDialog, removeDialog } = useDialog();
  // Track dialogs we push so they can't be orphaned on the app-root stack if
  // the screen unmounts (e.g. back-navigation) before they're dismissed.
  const dialogIdsRef = useRef<DialogId[]>([]);

  // Variables
  const { exerciseSetName, exerciseClass } = route.params;

  const DICTIONARY = { results: 'Ergebnisstatistik' };

  const handleWebClickDownload = (type: string) => {
    return () => {
      if (type === 'results' && exportRef.current) {
        exportAsImage(exportRef.current, 'results');
      }
    };
  };

  const showDownloadDialog = () => {
    const id = showDialog({
      title: 'Ergebnisstatistik herunterladen',
      content:
        'Wollen Sie Ihre Ergebnisstatistik wirklich herunterladen? Dies sollte nur auf eigenen Geräten durchgeführt werden, um zu verhindern, dass Unbefugte Zugriff auf die heruntergeladenen Daten erhalten.',
      confirmAction: {
        label: 'Herunterladen',
        onPress: (_e, onClose) => {
          onClose?.();
          handleWebClickDownload('results')();
        },
      },
      cancelAction: {
        label: 'Abbrechen',
        onPress: (_e, onClose) => onClose?.(),
      },
      showCancelButton: true,
    });
    dialogIdsRef.current.push(id);
  };

  const showMobileRedirectDialog = () => {
    const link = isPatientApp
      ? Links.aphasieBrowserApp
      : Links.therapistBrowserApp;
    const id = showDialog({
      title: 'Nur in der Browser-App möglich',
      confirmAction: {
        label: 'Schließen',
        onPress: (_e, onClose) => onClose?.(),
      },
      contentComponent: () => (
        <View style={[styles.innerStyle, styles.padding7]}>
          <Text style={{ fontSize: theme.fontSizes.xl }}>
            Das Herunterladen der {dataType.current} ist in der Browser-App am
            Computer auf{' '}
            <Text
              style={[styles.textStyle, { fontSize: theme.fontSizes.xl }]}
              onPress={() => Linking.openURL(link.href)}
            >
              {link.label}
            </Text>{' '}
            unter “Ergebnisse ansehen” möglich.
          </Text>
        </View>
      ),
    });
    dialogIdsRef.current.push(id);
  };

  const handleDownloadLinksClick = (type: string) => {
    if (Platform.OS === 'web') {
      dataType.current = '';
      if (type === 'results') {
        showDownloadDialog();
      }
    } else {
      dataType.current = DICTIONARY[type as keyof typeof DICTIONARY] ?? '';
      showMobileRedirectDialog();
    }
  };

  useEffect(() => {
    if (handleDownloadLinksClickState) {
      changeHandleDownloadLinksClickState(false);
      handleDownloadLinksClick('results');
    }
  }, [handleDownloadLinksClickState]);

  // Remove any dialogs we opened when leaving the screen, so they don't linger
  // on the app-root stack after navigation.
  useEffect(() => {
    const ids = dialogIdsRef.current;
    return () => {
      ids.forEach((id) => removeDialog(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportAsImage = async (
    el: HTMLIFrameElement,
    imageFileName: string,
  ) => {
    const windowX = el.contentWindow;
    html2canvas(windowX?.document?.body, {
      allowTaint: true,
      useCORS: true,
    }).then(function (canvas: HTMLCanvasElement) {
      canvas.style.display = 'none';
      try {
        if (window === undefined) {
          return;
        }
        window?.document?.body?.appendChild(canvas);
        const image = canvas
          .toDataURL('image/png')
          .replace('image/png', 'image/octet-stream');
        const a = window?.document?.createElement('a');
        a.setAttribute('download', imageFileName + '.png');
        a.setAttribute('href', image);
        a.click();
      } catch (error) {
        logError('Image export failed', error);
      }
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.innerStyle}>
        <CustomWebView
          ref={exportRef}
          individualStatsData={individualStatsData}
          timeSeriesData={timeSeriesData}
          appDisplayName={exerciseSetName}
          setType={exerciseClass === 'wordset' ? 'Wortset' : 'Satzset'}
          webViewTitle={webViewTitle}
        />
      </View>
    </View>
  );
};
